import { Box, Divider, FormControlLabel, Grid, Switch } from '@mui/material';
import { GridColumn } from '@progress/kendo-react-grid';
import { Form, Formik, FormikProps } from 'formik';
import { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  ActionButton,
  PrimaryButton,
  TertiaryButton
} from 'src/components/shared/Button/Button';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { Autocomplete } from 'src/components/shared/Forms/Autocomplete/Autocomplete';
import { FormField } from 'src/components/shared/Forms/FormHelpers';
import { InputField } from 'src/components/shared/Forms/Input/InputField';
import { ModalFooter } from 'src/components/shared/Modal/Modal';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { selectInstrumentGroups } from 'src/store/instrumentgroups/instrumentgroups.selectors';
import {
  InstrumentDetailDto,
  InstrumentParameterDto
} from 'src/store/instruments/instrument';
import { MethodDto } from 'src/store/methods/method';
import { selectMethods } from 'src/store/methods/methods.selectors';
import { ParameterDto } from 'src/store/parameters/parameter';
import { selectActiveParameters } from 'src/store/parameters/parameters.selectors';
import {
  maxLength,
  required,
  runValidations,
  validationRunner
} from 'src/utils/validation';
import styled from 'styled-components';

type Props = {
  instrument: Nullable<InstrumentDetailDto>;
  onCancel: () => void;
  onSubmit: (instrument: InstrumentDetailDto) => void;
};

export const InstrumentForm = forwardRef<
  FormikProps<InstrumentDetailDto>,
  Props
>((props, ref) => {
  const { onSubmit, onCancel } = props;

  const [showValidationErrors, setShowValidationErrors] =
    useState<boolean>(false);
  const [instrument, setInstrument] = useState<InstrumentDetailDto>(
    props.instrument || ({ active: true } as InstrumentDetailDto)
  );
  const [parameters, setParameters] = useState<ParameterDto[]>([]);
  const [method, setMethod] = useState<Nullable<MethodDto>>(null);
  const { t } = useTranslation();

  const instrumentGroups = useSelector(selectInstrumentGroups);
  const methods = useSelector(selectMethods);
  const parameters$ = useSelector(selectActiveParameters);

  const validate = (instrument: InstrumentDetailDto) => {
    const validationRunners = [
      validationRunner('name', t('props.name'), required, maxLength(50)),
      validationRunner('description', t('props.description'), maxLength(200)),
      validationRunner(
        'instrumentGroupId',
        t('instrumentGroup.singular'),
        required
      )
    ];
    return runValidations(instrument, validationRunners);
  };

  useEffect(() => {
    setParameters(parameters$);
  }, [parameters$]);

  const onAddParameter = (
    instrument: InstrumentDetailDto,
    parameter: ParameterDto,
    setFieldValue: any,
    setStatus: (status?: any) => void
  ) => {
    if (method === null) {
      setStatus(t('instrument.msgNoMethod'));
    } else if (
      combinationParameterMethodExists(
        instrument.instrumentParameters ?? [],
        parameter,
        method
      )
    ) {
      setStatus(t('instrument.msgCombinationExists'));
    } else {
      const instrumentParameters = [
        ...(instrument.instrumentParameters ?? []),
        {
          instrumentId: instrument.id,
          parameterId: parameter.id,
          methodId: method.id,
          parameterName: parameter.name,
          methodName: method.name
        } as InstrumentParameterDto
      ];
      setInstrument({
        ...instrument,
        instrumentParameters: instrumentParameters
      });
      setFieldValue('instrumentParameters', instrumentParameters);
    }
  };

  const combinationParameterMethodExists = (
    instrumentParameters: InstrumentParameterDto[],
    parameter: ParameterDto,
    method: MethodDto
  ) => {
    return instrumentParameters.some(
      ip => ip.parameterId === parameter.id && ip.methodId === method.id
    );
  };

  const onRemoveParameter = (
    instrument: InstrumentDetailDto,
    instrumentParameter: InstrumentParameterDto,
    setFieldValue: any
  ) => {
    const instrumentParameters = instrument.instrumentParameters.filter(
      ip =>
        !(
          ip.parameterId === instrumentParameter.parameterId &&
          ip.methodId === instrumentParameter.methodId
        )
    );
    setInstrument({
      ...instrument,
      instrumentParameters: instrumentParameters
    });
    setFieldValue('instrumentParameters', instrumentParameters);
  };

  return (
    <Formik
      initialValues={instrument}
      validate={validate}
      innerRef={ref}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(values);
        setSubmitting(false);
      }}
    >
      {({
        values,
        errors,
        status,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        setStatus,
        isSubmitting,
        submitCount
      }) => (
        <Form onSubmit={handleSubmit}>
          <Grid container>
            <Grid item xs={6}>
              <InputField
                name="name"
                label={t('props.name')}
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                errorMessage={errors.name}
                displayError={touched.name || submitCount > 0}
              />
            </Grid>
            <Grid item xs={6}>
              <FormField>
                <Autocomplete
                  options={instrumentGroups}
                  label={t('instrumentGroup.singular')}
                  optionField="name"
                  value={values.instrumentGroup}
                  onChange={instrumentGroup =>
                    setFieldValue('instrumentGroupId', instrumentGroup?.id)
                  }
                  onBlur={handleBlur}
                  errorMessage={errors.instrumentGroupId}
                  displayError={
                    !!errors.instrumentGroupId && showValidationErrors
                  }
                />
              </FormField>
            </Grid>
          </Grid>
          <InputField
            name="description"
            label={t('props.description')}
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
            errorMessage={errors.description}
            displayError={touched.description || submitCount > 0}
          />
          <div>
            <FormControlLabel
              className="mb-24"
              control={
                <Switch
                  checked={values.active}
                  onChange={handleChange}
                  name="active"
                />
              }
              label={t('props.active')}
            />
          </div>

          <Divider className="mb-32" />

          <Grid container>
            <Grid item xs={12}>
              <Autocomplete
                className="mb-16"
                options={methods}
                label={t('method.singular')}
                optionField="name"
                value={method}
                onChange={value => {
                  setMethod(value);
                  value && setStatus(null);
                }}
                onBlur={handleBlur}
              />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="center">
                {status && <StatusMessage>{status}</StatusMessage>}
              </Box>
            </Grid>
            <Grid item xs={8}>
              <DataGrid
                items={values.instrumentParameters}
                style={{ height: 400 }}
                initialSort={[{ field: 'parameterName', dir: 'asc' }]}
              >
                <GridColumn
                  field="parameterName"
                  title={t('parameter.singular')}
                />
                <GridColumn
                  field="methodName"
                  title={t('method.singular')}
                  cell={props => (
                    <td>
                      <div className="flex jc-sb align-center">
                        <BodyType truncate style={{ maxWidth: '80%' }}>
                          {props.dataItem.methodName}
                        </BodyType>
                        <ActionButton
                          icon="la la-times"
                          onClick={() =>
                            onRemoveParameter(
                              values,
                              props.dataItem,
                              setFieldValue
                            )
                          }
                        />
                      </div>
                    </td>
                  )}
                />
              </DataGrid>
            </Grid>

            <Grid item xs={4}>
              <DataGrid
                items={parameters}
                style={{ height: 400 }}
                initialSort={[{ field: 'name', dir: 'asc' }]}
              >
                <GridColumn
                  field="name"
                  title={t('parameter.all')}
                  cell={props => (
                    <td>
                      <div className="flex jc-sb align-center">
                        <BodyType truncate style={{ maxWidth: '80%' }}>
                          {props.dataItem.name}
                        </BodyType>
                        <ActionButton
                          icon="la la-plus"
                          onClick={() =>
                            onAddParameter(
                              values,
                              props.dataItem,
                              setFieldValue,
                              setStatus
                            )
                          }
                        />
                      </div>
                    </td>
                  )}
                />
              </DataGrid>
            </Grid>
          </Grid>

          <ModalFooter>
            <TertiaryButton
              onClick={onCancel}
              label={t('cancel')}
              className="mr-32"
            />
            <PrimaryButton
              type="submit"
              label={t('save.verb')}
              disabled={isSubmitting}
              onClick={() => {
                setShowValidationErrors(true);
              }}
            />
          </ModalFooter>
        </Form>
      )}
    </Formik>
  );
});

const StatusMessage = styled.span`
  color: var(--red-status-color);
`;
